import { ReactNode, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PopUpType } from '../../common/components/PopUp';
import { DeceasedFormData } from '../../api/deceasedApi';
import { useApi } from '../../api/useApi';
import { useNavigate } from 'react-router';
import { useReferences } from '../../helpers/context_manager/useReferences';
import { toOptions } from '../../helpers/toOptions';
import { formatRuianAddress } from '../../api/ruianApi';
import { validateDateAfterOrEqual, validateDateBeforeOrEqual, validateMaxDate } from '../../common/components/Form';

type FormValues = {
  title_before: string;
  title_after: string;
  first_name: string;
  last_name: string;
  is_unknown: boolean;
  gender: number;
  date_of_birth: string;
  date_of_death: string;
  birth_id: string;
  insurance_number: string;
  age_years: string;
  age_of_death: string;
  foreigner: boolean;
  insurance_company: number;
  residence_address: string;
  contact_address: string;
  residence_ruian_address: number | null;
  contact_ruian_address: number | null;
  note: string;
};

type UseDeceasedProps = {
  deceasedId?: number;
};

export const useDeceased = (props: UseDeceasedProps) => {
  const api = useApi();
  const { deceasedId } = props;
  const navigate = useNavigate();
  const { references, loading: referencesLoading } = useReferences();
  const [message, setMessage] = useState<{ type: PopUpType; message: ReactNode } | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);

  const methods = useForm<FormValues>({
    defaultValues: {
      title_before: '',
      title_after: '',
      first_name: '',
      last_name: '',
      is_unknown: false,
      gender: 0,
      date_of_birth: '',
      date_of_death: '',
      birth_id: '',
      insurance_number: '',
      age_years: '',
      age_of_death: '',
      foreigner: false,
      insurance_company: 0,
      residence_address: '',
      contact_address: '',
      residence_ruian_address: null,
      contact_ruian_address: null,
      note: ''
    },
    mode: 'onChange'
  });

  const isUnknown = methods.watch('is_unknown');
  const watchedBirthId = methods.watch('birth_id');
  const watchedInsuranceNumber = methods.watch('insurance_number');

  useEffect(() => {
    if (isUnknown || watchedBirthId) {
      methods.clearErrors('insurance_number');
    }
    if (isUnknown || watchedInsuranceNumber) {
      methods.clearErrors('birth_id');
    }
  }, [isUnknown, watchedBirthId, watchedInsuranceNumber]);

  useEffect(() => {
    if (deceasedId && !referencesLoading && references.genders.length > 0) {
      loadDeceasedData(Number(deceasedId));
    }
  }, [deceasedId, referencesLoading, references.genders.length]);

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'is_unknown' && value.is_unknown) {
        methods.setValue('birth_id', '');
        methods.setValue('insurance_number', '');
        methods.clearErrors(['date_of_birth', 'insurance_company']);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const loadRuianOptions = async (inputValue: string) => {
    if (inputValue.length < 2) return [];
    const res = await api.ruian.get({ search: inputValue, limit: 30 });
    return res.data.results.map((a) => ({ value: a.id, label: formatRuianAddress(a) }));
  };

  const loadInitialRuianOption = async (id: number) => {
    const res = await api.ruian.getOneById(id);
    return { value: res.data.id, label: formatRuianAddress(res.data) };
  };

  const computeBabyAge = (birthDate: string, deathDate: string): string | null => {
    if (!birthDate || !deathDate) return null;
    const birth = new Date(birthDate);
    const death = new Date(deathDate);

    let ageYears = death.getFullYear() - birth.getFullYear();
    const md = death.getMonth() - birth.getMonth();
    if (md < 0 || (md === 0 && death.getDate() < birth.getDate())) ageYears--;
    if (ageYears !== 0) return null;

    let months =
      (death.getFullYear() - birth.getFullYear()) * 12 +
      (death.getMonth() - birth.getMonth());
    if (death.getDate() < birth.getDate()) months--;
    if (months < 0) months = 0;

    const afterMonths = new Date(birth);
    afterMonths.setMonth(afterMonths.getMonth() + months);
    const remainingDays = Math.floor(
      (death.getTime() - afterMonths.getTime()) / 86400000
    );
    const weeks = Math.floor(remainingDays / 7);

    const monthLabel = months === 1 ? 'měsíc' : months < 5 ? 'měsíce' : 'měsíců';
    const weekLabel = weeks === 1 ? 'týden' : weeks < 5 ? 'týdny' : 'týdnů';

    if (months === 0) return `${weeks} ${weekLabel}`;
    return `${months} ${monthLabel} a ${weeks} ${weekLabel}`;
  };

  const calculateAge = (birthDate: string, deathDate: string): number | null => {
    if (!birthDate || !deathDate) return null;

    const birth = new Date(birthDate);
    const death = new Date(deathDate);

    let age = death.getFullYear() - birth.getFullYear();
    const monthDiff = death.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && death.getDate() < birth.getDate())) {
      age--;
    }

    return age >= 0 ? age : null;
  };

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'date_of_birth' || name === 'date_of_death') {
        const age = calculateAge(value.date_of_birth || '', value.date_of_death || '');
        if (age !== null) {
          methods.setValue('age_years', age.toString());
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const loadDeceasedData = async (deceasedId: number) => {
    setLoading(true);
    try {
      const response = await api.deceased.getOneById(deceasedId);
      const data = response.data;

      methods.reset({
        title_before: data.title_before || '',
        title_after: data.title_after || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        is_unknown: data.is_unknown || false,
        gender: data.gender || 0,
        date_of_birth: data.date_of_birth || '',
        date_of_death: data.date_of_death || '',
        birth_id: data.birth_id || '',
        insurance_number: data.insurance_number || '',
        age_years: data.age_years?.toString() || '',
        foreigner: false,
        insurance_company: data.insurance_company || 0,
        residence_address: data.residence_address || '',
        contact_address: data.contact_address || '',
        residence_ruian_address: data.residence_ruian_address || null,
        contact_ruian_address: data.contact_ruian_address || null,
        note: data.note || ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        message: 'Nepodařilo se načíst data zemřelého.'
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const formData: DeceasedFormData = {
        title_before: data.title_before,
        title_after: data.title_after,
        first_name: data.first_name,
        last_name: data.last_name,
        is_unknown: data.is_unknown,
        gender: data.gender,
        date_of_birth: data.date_of_birth || undefined,
        date_of_death: data.date_of_death,
        birth_id: data.birth_id || undefined,
        insurance_number: data.insurance_number || undefined,
        age_years: data.age_years ? Number(data.age_years) : undefined,
        insurance_company:
          Array.isArray(data.insurance_company) && data.insurance_company.length === 0
            ? undefined
            : data.insurance_company,
        residence_address: data.residence_address,
        contact_address: data.contact_address,
        residence_ruian_address: data.residence_ruian_address || undefined,
        contact_ruian_address: data.contact_ruian_address || undefined,
        note: data.note
      };

      if (deceasedId) {
        await api.deceased.patch(Number(deceasedId), formData);
        setMessage({
          type: 'success',
          message: 'Zemřelý byl úspěšně aktualizován.'
        });
      } else {
        const response = await api.deceased.post(formData);
        const newDeceasedId = response.data.id;

        // Skontrolovať či už existuje protokol pre nového zomrelého
        const protocolsResponse = await api.protocols.get({
          deceased: newDeceasedId // TODO potrebne doplnit do params
        });

        setMessage({
          type: 'success',
          message: 'Zemřelý byl úspěšně vytvořen.'
        });

        if (newDeceasedId) {
          // Protokol neexistuje - vytvoriť nový
          const currentYear = new Date().getFullYear();
          const newProtocol = await api.protocols.post({
            deceased: newDeceasedId,
            protocol_year: currentYear,
            date_of_death: formData.date_of_death,
            note: formData.note
          });

          // Create LabMats record for the new protocol
          await api.labmats.post({
            autopsy_protocol: newProtocol.data.id
          });

          // Delay - zobrazení úspěšné zprávy pro uživatele
          setTimeout(() => {
            navigate(`/protocol/${newProtocol.data.id}/protocol`);
          }, 1500);
        } else {
          // Protokol už existuje - presmerovať na existujúci
          const existingProtocolId = protocolsResponse.data.results[0].id;

          // Delay - zobrazení úspěšné zprávy pro uživatele
          setTimeout(() => {
            navigate(`/protocol/${existingProtocolId}/protocol`);
          }, 1500);
        }
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      let errorMessage = 'Nepodařilo se uložit data.';

      // Validation errors from API?
      if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
        errorMessage = Object.values(errorData).flat().join('. ');
      }

      setMessage({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const togglePopup = () => {
    setMessage(undefined);
  };

  const handleFindFromUnis = async () => {
    const formValues = methods.getValues();

    if (!formValues.birth_id && !formValues.insurance_number) {
      setMessage({
        type: 'info',
        message: 'Zadejte prosím RČ nebo číslo pojistky pro vyhledání.'
      });
      return;
    }

    setLoading(true);
    try {
      const identifyData: DeceasedFormData = {
        birth_id: formValues.birth_id || undefined,
        insurance_number: formValues.insurance_number || undefined
      };

      const response = await api.deceased.identify(identifyData);
      const data = response.data.deceased;

      methods.reset({
        title_before: data.title_before || '',
        title_after: data.title_after || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        is_unknown: data.is_unknown || false,
        gender: data.gender || 0,
        date_of_birth: data.date_of_birth || '',
        date_of_death: data.date_of_death || '',
        birth_id: data.birth_id || formValues.birth_id,
        insurance_number: data.insurance_number || formValues.insurance_number,
        age_years: data.age_years?.toString() || '',
        foreigner: false,
        insurance_company: data.insurance_company || 0,
        residence_address: data.residence_address || '',
        contact_address: data.contact_address || '',
        note: data.note || ''
      });

      setMessage({
        type: 'success',
        message: 'Údaje byly úspěšně nalezeny a předvyplněny.'
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Nepodařilo se nalézt údaje v UNISu.';
      setMessage({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const watchedDateOfBirth = methods.watch('date_of_birth');
  const watchedDateOfDeath = methods.watch('date_of_death');
  const babyAge = computeBabyAge(watchedDateOfBirth, watchedDateOfDeath);

  const fields = {
    title_before: {
      id: 'title_before',
      name: 'title_before',
      label: 'Titul před'
    },
    title_after: {
      id: 'title_after',
      name: 'title_after',
      label: 'Titul za'
    },
    first_name: {
      id: 'first_name',
      name: 'first_name',
      label: 'Jméno',
      required: false
    },
    last_name: {
      id: 'last_name',
      name: 'last_name',
      label: 'Příjmení',
      required: false
    },
    date_of_birth: {
      id: 'date_of_birth',
      name: 'date_of_birth',
      label: 'Datum narození',
      type: 'date',
      required: !isUnknown,
      validate: [
        validateDateBeforeOrEqual('date_of_death', 'Datum narození nesmí být po datu úmrtí'),
        validateMaxDate(new Date(), 'Datum narození nemůže být v budoucnosti')
      ]
    },
    date_of_death: {
      id: 'date_of_death',
      name: 'date_of_death',
      label: 'Datum úmrtí',
      type: 'date',
      required: true,
      validate: [
        validateDateAfterOrEqual('date_of_birth', 'Datum úmrtí nesmí být před datem narození'),
        validateMaxDate(new Date(), 'Datum úmrtí nemůže být v budoucnosti')
      ]
    },
    birth_id: {
      id: 'birth_id',
      name: 'birth_id',
      label: 'Rodné číslo',
      required: !isUnknown
    },
    insurance_number: {
      id: 'insurance_number',
      name: 'insurance_number',
      label: 'Číslo pojistky',
      required: false
    },
    age_years: {
      id: 'age_years',
      name: 'age_years',
      label: 'Věk'
    },
    gender: {
      id: 'gender',
      name: 'gender',
      label: 'Pohlaví',
      required: true
    },
    insurance_company: {
      id: 'insurance_company',
      name: 'insurance_company',
      label: 'Pojišťovna',
      required: !isUnknown
    },
    residence_address: {
      id: 'residence_address',
      name: 'residence_address',
      label: 'Bydliště'
    },
    contact_address: {
      id: 'contact_address',
      name: 'contact_address',
      label: 'Kontakt'
    },
    residence_ruian_address: {
      id: 'residence_ruian_address',
      name: 'residence_ruian_address',
      label: 'Adresa bydliště'
    },
    contact_ruian_address: {
      id: 'contact_ruian_address',
      name: 'contact_ruian_address',
      label: 'Kontaktní adresa'
    },
    note: {
      id: 'note',
      name: 'note',
      label: 'Poznámka',
      required: isUnknown
    }
  };

  return {
    methods,
    fields,
    message,
    loading: loading || referencesLoading,
    options: {
      genders: toOptions(references.genders, 'id'),
      insurances: toOptions(references.insurances, 'name')
    },
    onSubmit,
    togglePopup,
    handleFindFromUnis,
    loadRuianOptions,
    loadInitialRuianOption,
    isEditMode: !!deceasedId,
    babyAge
  };
};
